import { useEffect, useState } from "react";
import { MapContainer, TileLayer, LayersControl, LayerGroup, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "@/lib/api";
import { Warning, Fire, Drop, Wind } from "@phosphor-icons/react";

// PulseMap — Leaflet map with togglable overlays for air quality, wildfire,
// and flood/hazard data. Base map = OpenStreetMap. AQI heat spots from
// Open-Meteo Air Quality (sampled around the centre). Fires: NASA FIRMS via
// its public GeoJSON snapshot (last 24 h VIIRS active fires, no key required).
// Floods and other hazards come from our existing /api/civic-reports.

// Fix Leaflet default marker paths (CRA doesn't resolve them by default)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Sample AQI at 5 nearby offsets — one call each. Fast enough for a widget.
async function sampleAqi(center, radiusDeg = 0.35) {
  const points = [
    { lat: center.lat, lon: center.lon },
    { lat: center.lat + radiusDeg, lon: center.lon },
    { lat: center.lat - radiusDeg, lon: center.lon },
    { lat: center.lat, lon: center.lon + radiusDeg },
    { lat: center.lat, lon: center.lon - radiusDeg },
  ];
  const out = [];
  await Promise.all(points.map(async (p) => {
    try {
      const url = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${p.lat}&longitude=${p.lon}&current=us_aqi`;
      const r = await fetch(url);
      const j = await r.json();
      const aqi = j?.current?.us_aqi;
      if (aqi != null) out.push({ ...p, aqi });
    } catch {}
  }));
  return out;
}

// Fire snapshot from FIRMS. Public GeoJSON (last 24 h VIIRS). Small enough to
// filter client-side by bbox.
async function loadFires(center, halfDeg = 8) {
  try {
    const url = "https://firms.modaps.eosdis.nasa.gov/api/area/csv/DEMO_KEY/VIIRS_SNPP_NRT/world/1";
    const r = await fetch(url, { cache: "force-cache" });
    if (!r.ok) throw new Error("no data");
    const text = await r.text();
    const lines = text.trim().split("\n");
    const header = lines.shift().split(",");
    const idxLat = header.indexOf("latitude");
    const idxLon = header.indexOf("longitude");
    const idxConf = header.indexOf("confidence");
    const idxAcq = header.indexOf("acq_date");
    if (idxLat < 0 || idxLon < 0) return [];
    return lines.slice(0, 4000).map((row) => {
      const cols = row.split(",");
      return { lat: parseFloat(cols[idxLat]), lon: parseFloat(cols[idxLon]),
               confidence: cols[idxConf], acq_date: cols[idxAcq] };
    }).filter((f) => !isNaN(f.lat) && !isNaN(f.lon) &&
                    Math.abs(f.lat - center.lat) < halfDeg &&
                    Math.abs(f.lon - center.lon) < halfDeg);
  } catch { return []; }
}

const AQI_COLOR = (aqi) =>
  aqi == null ? "#94a3b8" :
  aqi <= 50   ? "#16a34a" :
  aqi <= 100  ? "#eab308" :
  aqi <= 150  ? "#f97316" :
  aqi <= 200  ? "#ef4444" : "#7f1d1d";

const HAZARD_COLOR = {
  wildfire:    "#dc2626",
  flood:       "#2563eb",
  heatwave:    "#f97316",
  cyclone:     "#7c3aed",
  "air-quality":"#eab308",
  landslide:   "#78350f",
  drought:     "#a16207",
};

export default function PulseMap({ center }) {
  const [aqi, setAqi] = useState([]);
  const [fires, setFires] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    let alive = true;
    sampleAqi(center).then((v) => alive && setAqi(v));
    loadFires(center).then((v) => alive && setFires(v));
    http.get("/civic-reports?limit=200")
      .then((r) => alive && setReports((r.data?.reports || []).filter((x) => x.lat != null && x.lon != null)))
      .catch(() => alive && setReports([]));
    return () => { alive = false; };
  }, [center.lat, center.lon]);

  return (
    <div data-testid="pulse-map" className="rounded-2xl overflow-hidden border border-black/10 shadow-inner" style={{ height: 380 }}>
      <MapContainer
        center={[center.lat, center.lon]} zoom={7} style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Terrain">
            <TileLayer
              attribution='&copy; OpenStreetMap · Terrain'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Street">
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay checked name={`Air Quality (${aqi.length})`}>
            <LayerGroup>
              {aqi.map((p, i) => (
                <CircleMarker
                  key={`aqi-${i}`} center={[p.lat, p.lon]} radius={22}
                  pathOptions={{ color: AQI_COLOR(p.aqi), weight: 2, fillOpacity: 0.28 }}
                >
                  <Popup>
                    <div className="text-sm"><b>AQI {Math.round(p.aqi)}</b></div>
                    <div className="text-xs text-gray-600">{p.lat.toFixed(2)}, {p.lon.toFixed(2)}</div>
                    <div className="text-xs text-gray-600">Source: Open-Meteo</div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name={`Wildfires 24h (${fires.length})`}>
            <LayerGroup>
              {fires.map((f, i) => (
                <CircleMarker
                  key={`fire-${i}`} center={[f.lat, f.lon]} radius={4}
                  pathOptions={{ color: "#dc2626", fillColor: "#f97316", weight: 1, fillOpacity: 0.85 }}
                >
                  <Popup>
                    <div className="text-sm"><b>Active fire</b></div>
                    <div className="text-xs text-gray-600">{f.acq_date} · conf {f.confidence}</div>
                    <div className="text-xs text-gray-600">Source: NASA FIRMS (VIIRS)</div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked name={`Hazards (${reports.length})`}>
            <LayerGroup>
              {reports.map((r, i) => (
                <CircleMarker
                  key={`rep-${i}`} center={[r.lat, r.lon]} radius={8}
                  pathOptions={{
                    color: HAZARD_COLOR[r.hazard] || "#374151",
                    fillColor: HAZARD_COLOR[r.hazard] || "#6b7280",
                    weight: 2, fillOpacity: 0.75,
                  }}
                >
                  <Popup>
                    <div className="text-sm"><b>{r.hazard} · {r.severity}</b></div>
                    <div className="text-xs">{r.location}</div>
                    <div className="text-xs text-gray-700 mt-1">{r.description}</div>
                    <div className="text-xs text-gray-500 mt-1">Source: {r.reporter_name}</div>
                  </Popup>
                </CircleMarker>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>
      </MapContainer>
    </div>
  );
}
